import type {
    Bi,
    Gender,
    Gana,
    Lakara,
    Pada,
    PadaClass,
    Purusha,
    ReviewStatus,
    SkillId,
    Vacana,
    Vibhakti
} from '@/sanskrit/lib/sanskrit/types'

export interface GrammarLabel {
    sa: string
    short: string
    iast: string
    en: string
    hi: string
    desc: Bi
}

export const PURUSHA_LABEL: Record<Purusha, GrammarLabel> = {
    prathama: {
        sa: 'प्रथमपुरुषः',
        short: 'प्रथम',
        iast: 'prathama-puruṣa',
        en: 'Third person',
        hi: 'अन्य पुरुष (वह / वे)',
        desc: {
            hi: 'जिसके बारे में बात हो रही है: वह, वे, बालक, फल... (अंग्रेज़ी में "third person")',
            en: 'The one spoken about: he, she, it, they, any noun. (English calls this "third person".)'
        }
    },
    madhyama: {
        sa: 'मध्यमपुरुषः',
        short: 'मध्यम',
        iast: 'madhyama-puruṣa',
        en: 'Second person',
        hi: 'मध्यम पुरुष (तुम / आप)',
        desc: {
            hi: 'जिससे बात की जा रही है: तुम (त्वम्, युवाम्, यूयम्)',
            en: 'The one spoken to: you (त्वम्, युवाम्, यूयम्).'
        }
    },
    uttama: {
        sa: 'उत्तमपुरुषः',
        short: 'उत्तम',
        iast: 'uttama-puruṣa',
        en: 'First person',
        hi: 'उत्तम पुरुष (मैं / हम)',
        desc: {
            hi: 'जो स्वयं बोल रहा है: मैं, हम (अहम्, आवाम्, वयम्)',
            en: 'The speaker: I, we (अहम्, आवाम्, वयम्). (English calls this "first person".)'
        }
    }
}

export const VACANA_LABEL: Record<Vacana, GrammarLabel> = {
    eka: {
        sa: 'एकवचनम्',
        short: 'एक',
        iast: 'ekavacana',
        en: 'Singular (one)',
        hi: 'एकवचन (एक)',
        desc: { hi: 'केवल एक व्यक्ति या वस्तु', en: 'Exactly one person or thing' }
    },
    dvi: {
        sa: 'द्विवचनम्',
        short: 'द्वि',
        iast: 'dvivacana',
        en: 'Dual (two)',
        hi: 'द्विवचन (दो)',
        desc: { hi: 'ठीक दो व्यक्ति या वस्तुएँ', en: 'Exactly two people or things' }
    },
    bahu: {
        sa: 'बहुवचनम्',
        short: 'बहु',
        iast: 'bahuvacana',
        en: 'Plural (three or more)',
        hi: 'बहुवचन (तीन या अधिक)',
        desc: { hi: 'तीन या उससे अधिक', en: 'Three or more' }
    }
}

export interface VibhaktiLabel extends GrammarLabel {
    karaka: string
    role: Bi
    marker: Bi
    question: Bi
}

export const VIBHAKTI_LABEL: Record<Vibhakti, VibhaktiLabel> = {
    1: {
        sa: 'प्रथमा',
        short: 'प्र०',
        iast: 'prathamā',
        en: 'Nominative',
        hi: 'प्रथमा विभक्ति',
        karaka: 'कर्ता',
        role: { hi: 'कर्ता: काम करने वाला', en: 'Subject: the one who does the action' },
        marker: { hi: 'ने / (कोई चिह्न नहीं)', en: '(subject, no preposition)' },
        question: { hi: 'कौन?', en: 'Who? What?' },
        desc: {
            hi: 'वाक्य का कर्ता (करने वाला) प्रथमा में होता है। जैसे: रामः पठति।',
            en: 'The doer of the sentence stands in प्रथमा. Example: रामः पठति (Rāma reads).'
        }
    },
    2: {
        sa: 'द्वितीया',
        short: 'द्वि०',
        iast: 'dvitīyā',
        en: 'Accusative',
        hi: 'द्वितीया विभक्ति',
        karaka: 'कर्म',
        role: { hi: 'कर्म: जिस पर क्रिया का फल पड़े', en: 'Object: what the action is done to' },
        marker: { hi: 'को', en: '(direct object), to (a place)' },
        question: { hi: 'किसको? क्या?', en: 'Whom? What? Where to?' },
        desc: {
            hi: 'कर्म (जिस पर काम हो) द्वितीया में होता है। जैसे: रामः पुस्तकं पठति।',
            en: 'The object stands in द्वितीया. Example: रामः पुस्तकं पठति (Rāma reads a book).'
        }
    },
    3: {
        sa: 'तृतीया',
        short: 'तृ०',
        iast: 'tṛtīyā',
        en: 'Instrumental',
        hi: 'तृतीया विभक्ति',
        karaka: 'करणम्',
        role: { hi: 'करण: जिसके द्वारा काम हो', en: 'Instrument: the means by which it is done' },
        marker: { hi: 'से, के द्वारा', en: 'with, by means of' },
        question: { hi: 'किससे? किसके द्वारा?', en: 'With what? By whom?' },
        desc: {
            hi: 'साधन (जिससे काम हो) तृतीया में। जैसे: सः लेखन्या लिखति।',
            en: 'The instrument stands in तृतीया. Example: सः लेखन्या लिखति (He writes with a pen).'
        }
    },
    4: {
        sa: 'चतुर्थी',
        short: 'च०',
        iast: 'caturthī',
        en: 'Dative',
        hi: 'चतुर्थी विभक्ति',
        karaka: 'सम्प्रदानम्',
        role: { hi: 'सम्प्रदान: जिसे कुछ दिया जाए / जिसके लिए', en: 'Recipient: to whom or for whom' },
        marker: { hi: 'को, के लिए', en: 'to, for' },
        question: { hi: 'किसके लिए? किसको (देना)?', en: 'To whom? For whom?' },
        desc: {
            hi: 'जिसे दिया जाए वह चतुर्थी में। जैसे: नृपः ब्राह्मणाय धनं ददाति।',
            en: 'The recipient stands in चतुर्थी. Example: नृपः ब्राह्मणाय धनं ददाति (The king gives wealth to the brāhmaṇa).'
        }
    },
    5: {
        sa: 'पञ्चमी',
        short: 'प०',
        iast: 'pañcamī',
        en: 'Ablative',
        hi: 'पञ्चमी विभक्ति',
        karaka: 'अपादानम्',
        role: { hi: 'अपादान: जिससे अलग होना हो', en: 'Source: the point something moves away from' },
        marker: { hi: 'से (अलग होना)', en: 'from' },
        question: { hi: 'कहाँ से?', en: 'From where?' },
        desc: {
            hi: 'जिससे अलगाव हो वह पञ्चमी में। जैसे: वृक्षात् पत्रं पतति।',
            en: 'The point of separation stands in पञ्चमी. Example: वृक्षात् पत्रं पतति (A leaf falls from the tree).'
        }
    },
    6: {
        sa: 'षष्ठी',
        short: 'ष०',
        iast: 'ṣaṣṭhī',
        en: 'Genitive',
        hi: 'षष्ठी विभक्ति',
        karaka: 'सम्बन्धः',
        role: { hi: 'सम्बन्ध: किसका (यह कारक नहीं है)', en: 'Relation: whose (a relation, not a kāraka)' },
        marker: { hi: 'का, के, की', en: 'of, ’s' },
        question: { hi: 'किसका?', en: 'Whose? Of what?' },
        desc: {
            hi: 'सम्बन्ध षष्ठी में। जैसे: रामस्य पुस्तकम्। (सम्बन्ध कारक नहीं माना जाता।)',
            en: 'Possession and relation use षष्ठी. Example: रामस्य पुस्तकम् (Rāma’s book). Traditionally, relation is not counted as a kāraka.'
        }
    },
    7: {
        sa: 'सप्तमी',
        short: 'स०',
        iast: 'saptamī',
        en: 'Locative',
        hi: 'सप्तमी विभक्ति',
        karaka: 'अधिकरणम्',
        role: { hi: 'अधिकरण: आधार, स्थान या समय', en: 'Location: the place or time where it happens' },
        marker: { hi: 'में, पर', en: 'in, on, at' },
        question: { hi: 'कहाँ? कब?', en: 'Where? When?' },
        desc: {
            hi: 'आधार (स्थान) सप्तमी में। जैसे: वृक्षे खगः अस्ति।',
            en: 'The location stands in सप्तमी. Example: वृक्षे खगः अस्ति (There is a bird in the tree).'
        }
    },
    8: {
        sa: 'सम्बोधनम्',
        short: 'सं०',
        iast: 'sambodhana',
        en: 'Vocative',
        hi: 'सम्बोधन',
        karaka: 'सम्बोधनम्',
        role: { hi: 'सम्बोधन: किसी को पुकारना', en: 'Address: calling out to someone' },
        marker: { hi: 'हे, अरे', en: 'O …!' },
        question: { hi: 'किसे पुकारा?', en: 'Who is being called?' },
        desc: {
            hi: 'पुकारने के लिए। यह प्रथमा का ही एक रूप माना जाता है, पर तालिका में अलग दिखाया जाता है। जैसे: हे राम!',
            en: 'Used to call someone. Traditionally a use of the first case, shown as its own row by convention. Example: हे राम! (O Rāma!)'
        }
    }
}

export const GENDER_LABEL: Record<Gender, GrammarLabel> = {
    m: {
        sa: 'पुंलिङ्गम्',
        short: 'पुं०',
        iast: 'puṃliṅga',
        en: 'Masculine',
        hi: 'पुंलिङ्ग',
        desc: { hi: 'पुरुषवाचक लिङ्ग', en: 'Masculine gender' }
    },
    f: {
        sa: 'स्त्रीलिङ्गम्',
        short: 'स्त्री०',
        iast: 'strīliṅga',
        en: 'Feminine',
        hi: 'स्त्रीलिङ्ग',
        desc: { hi: 'स्त्रीवाचक लिङ्ग', en: 'Feminine gender' }
    },
    n: {
        sa: 'नपुंसकलिङ्गम्',
        short: 'नपुं०',
        iast: 'napuṃsakaliṅga',
        en: 'Neuter',
        hi: 'नपुंसकलिङ्ग',
        desc: { hi: 'न पुरुष, न स्त्री', en: 'Neither masculine nor feminine' }
    }
}

export interface LakaraLabel extends GrammarLabel {
    kala: string
    use: Bi
    example: { sa: string; hi: string; en: string }
    level: 'beginner' | 'intermediate' | 'advanced' | 'vedic'
}

export const LAKARA_LABEL: Record<Lakara, LakaraLabel> = {
    lat: {
        sa: 'लट्',
        short: 'लट्',
        iast: 'laṭ',
        en: 'Present',
        hi: 'वर्तमान काल',
        kala: 'वर्तमानकालः',
        level: 'beginner',
        use: {
            hi: 'जो काम अभी हो रहा है या सामान्य रूप से होता है।',
            en: 'An action happening now, or one that happens habitually.'
        },
        example: { sa: 'सः पठति।', hi: 'वह पढ़ता है।', en: 'He reads / is reading.' },
        desc: { hi: 'वर्तमान काल', en: 'Present tense' }
    },
    lrt: {
        sa: 'लृट्',
        short: 'लृट्',
        iast: 'lṛṭ',
        en: 'Simple future',
        hi: 'सामान्य भविष्यत् काल',
        kala: 'सामान्यभविष्यत्कालः',
        level: 'beginner',
        use: {
            hi: 'जो काम आगे होगा।',
            en: 'An action that will happen.'
        },
        example: { sa: 'सः पठिष्यति।', hi: 'वह पढ़ेगा।', en: 'He will read.' },
        desc: { hi: 'भविष्यत् काल', en: 'Future tense' }
    },
    lan: {
        sa: 'लङ्',
        short: 'लङ्',
        iast: 'laṅ',
        en: 'Imperfect past',
        hi: 'अनद्यतन भूत काल',
        kala: 'अनद्यतनभूतकालः',
        level: 'beginner',
        use: {
            hi: 'बीता हुआ काम (आज से पहले का)। पाठ्यपुस्तकों में सामान्य भूतकाल के लिए यही सबसे पहले सिखाया जाता है।',
            en: 'A past action, strictly one not of today. Textbooks teach it first as the everyday past tense.'
        },
        example: { sa: 'सः अपठत्।', hi: 'उसने पढ़ा।', en: 'He read.' },
        desc: { hi: 'भूत काल', en: 'Past tense' }
    },
    lot: {
        sa: 'लोट्',
        short: 'लोट्',
        iast: 'loṭ',
        en: 'Imperative',
        hi: 'आज्ञार्थ',
        kala: 'आज्ञार्थः',
        level: 'beginner',
        use: {
            hi: 'आज्ञा, प्रार्थना या अनुमति: पढ़ो, वह पढ़े, मैं पढ़ूँ?',
            en: 'Commands, requests and permission: read!, let him read, may I read?'
        },
        example: { sa: 'त्वं पठ।', hi: 'तुम पढ़ो।', en: 'You, read!' },
        desc: { hi: 'आज्ञार्थक', en: 'Imperative mood' }
    },
    vidhilin: {
        sa: 'विधिलिङ्',
        short: 'विधिलिङ्',
        iast: 'vidhiliṅ',
        en: 'Optative',
        hi: 'विधिलिङ् (चाहिए / सम्भावना)',
        kala: 'विध्यर्थः',
        level: 'beginner',
        use: {
            hi: 'उपदेश, कर्तव्य, सम्भावना: उसे पढ़ना चाहिए, शायद वह पढ़े।',
            en: 'Advice, duty, possibility: he should read, he might read.'
        },
        example: { sa: 'सः पठेत्।', hi: 'उसे पढ़ना चाहिए।', en: 'He should read.' },
        desc: { hi: 'विधि, सम्भावना', en: 'Optative mood' }
    },
    lit: {
        sa: 'लिट्',
        short: 'लिट्',
        iast: 'liṭ',
        en: 'Perfect (remote past)',
        hi: 'परोक्ष भूत काल',
        kala: 'परोक्षभूतकालः',
        level: 'intermediate',
        use: {
            hi: 'बहुत पहले का, वक्ता की आँखों से परे (परोक्ष) बीता काम। कथाओं और इतिहास में बहुत मिलता है।',
            en: 'A remote past action, not witnessed by the speaker. Very common in narratives and epics.'
        },
        example: { sa: 'रामः बभूव।', hi: 'राम हुए थे।', en: 'There was Rāma (long ago).' },
        desc: { hi: 'परोक्ष भूत', en: 'Perfect tense' }
    },
    lut: {
        sa: 'लुट्',
        short: 'लुट्',
        iast: 'luṭ',
        en: 'Periphrastic future',
        hi: 'अनद्यतन भविष्यत् काल',
        kala: 'अनद्यतनभविष्यत्कालः',
        level: 'intermediate',
        use: {
            hi: 'आज के बाद (कल या आगे) होने वाला निश्चित काम।',
            en: 'A definite future action beyond today.'
        },
        example: { sa: 'श्वः सः पठिता।', hi: 'कल वह पढ़ेगा।', en: 'Tomorrow he will read.' },
        desc: { hi: 'अनद्यतन भविष्यत्', en: 'Periphrastic future' }
    },
    lun: {
        sa: 'लुङ्',
        short: 'लुङ्',
        iast: 'luṅ',
        en: 'Aorist',
        hi: 'सामान्य भूत काल',
        kala: 'सामान्यभूतकालः',
        level: 'advanced',
        use: {
            hi: 'सामान्य भूतकाल, आज का बीता काम भी। इसके रूप धातु के अनुसार कई प्रकार से बनते हैं।',
            en: 'General past, including something done earlier today. It is formed in several different ways depending on the root.'
        },
        example: { sa: 'सः अभूत्।', hi: 'वह हुआ।', en: 'He became.' },
        desc: { hi: 'सामान्य भूत', en: 'Aorist' }
    },
    ashirlin: {
        sa: 'आशीर्लिङ्',
        short: 'आशीर्लिङ्',
        iast: 'āśīrliṅ',
        en: 'Benedictive',
        hi: 'आशीर्लिङ् (आशीर्वाद)',
        kala: 'आशीरर्थः',
        level: 'advanced',
        use: {
            hi: 'आशीर्वाद और शुभकामना: तुम्हारा भला हो।',
            en: 'Blessings and wishes: may you prosper.'
        },
        example: { sa: 'भवान् विजयी भूयात्।', hi: 'आप विजयी हों।', en: 'May you be victorious.' },
        desc: { hi: 'आशीर्वाद', en: 'Benedictive' }
    },
    lrn: {
        sa: 'लृङ्',
        short: 'लृङ्',
        iast: 'lṛṅ',
        en: 'Conditional',
        hi: 'हेतुहेतुमद् भूत (क्रियातिपत्ति)',
        kala: 'क्रियातिपत्तिः',
        level: 'advanced',
        use: {
            hi: 'यदि ऐसा होता तो वैसा होता: जो काम हो न सका।',
            en: 'Hypothetical: "if this had happened, that would have happened", an action that did not come about.'
        },
        example: { sa: 'सुवृष्टिः चेत् अभविष्यत्, तर्हि सुभिक्षम् अभविष्यत्।', hi: 'यदि अच्छी वर्षा होती, तो अन्न भरपूर होता।', en: 'If there had been good rain, there would have been plenty of food.' },
        desc: { hi: 'क्रियातिपत्ति', en: 'Conditional' }
    },
    let: {
        sa: 'लेट्',
        short: 'लेट्',
        iast: 'leṭ',
        en: 'Vedic subjunctive',
        hi: 'लेट् (केवल वेद में)',
        kala: 'वैदिकः',
        level: 'vedic',
        use: {
            hi: 'यह केवल वैदिक संस्कृत में मिलता है, लौकिक (Classical) संस्कृत में नहीं।',
            en: 'Found only in Vedic Sanskrit, never in Classical Sanskrit.'
        },
        example: { sa: 'प्रियः सूर्ये प्रियो अग्ना भवाति।', hi: '(वैदिक उदाहरण) प्रिय होवे।', en: '(Vedic example) may he be dear.' },
        desc: { hi: 'वैदिक लकार', en: 'Vedic only' }
    }
}

export const PADA_LABEL: Record<Pada, GrammarLabel> = {
    P: {
        sa: 'परस्मैपदम्',
        short: 'परस्मै०',
        iast: 'parasmaipada',
        en: 'Parasmaipada (active endings)',
        hi: 'परस्मैपद',
        desc: {
            hi: '"दूसरे के लिए शब्द": ति, तः, अन्ति... वाले प्रत्यय',
            en: '"A word for another": the endings ति, तः, अन्ति…'
        }
    },
    A: {
        sa: 'आत्मनेपदम्',
        short: 'आत्मने०',
        iast: 'ātmanepada',
        en: 'Ātmanepada (middle endings)',
        hi: 'आत्मनेपद',
        desc: {
            hi: '"अपने लिए शब्द": ते, इते, अन्ते... वाले प्रत्यय',
            en: '"A word for oneself": the endings ते, इते, अन्ते…'
        }
    }
}

export const PADA_CLASS_LABEL: Record<PadaClass, Bi & { sa: string }> = {
    parasmaipada: { sa: 'परस्मैपदी', hi: 'केवल परस्मैपद', en: 'Parasmaipada only' },
    atmanepada: { sa: 'आत्मनेपदी', hi: 'केवल आत्मनेपद', en: 'Ātmanepada only' },
    ubhayapada: { sa: 'उभयपदी', hi: 'दोनों पद', en: 'Both padas (ubhayapada)' }
}

export const GANA_LABEL: Record<Gana, { sa: string; iast: string; vikarana: string; en: string; hi: string }> = {
    1: { sa: 'भ्वादिगणः', iast: 'bhvādi', vikarana: 'शप् (अ)', en: 'Class 1 (bhū class)', hi: 'पहला गण (भू आदि)' },
    2: { sa: 'अदादिगणः', iast: 'adādi', vikarana: '(लुक्, कोई विकरण नहीं)', en: 'Class 2 (ad class)', hi: 'दूसरा गण (अद् आदि)' },
    3: { sa: 'जुहोत्यादिगणः', iast: 'juhotyādi', vikarana: '(श्लु, द्वित्व)', en: 'Class 3 (hu class)', hi: 'तीसरा गण (हु आदि)' },
    4: { sa: 'दिवादिगणः', iast: 'divādi', vikarana: 'श्यन् (य)', en: 'Class 4 (div class)', hi: 'चौथा गण (दिव् आदि)' },
    5: { sa: 'स्वादिगणः', iast: 'svādi', vikarana: 'श्नु (नु)', en: 'Class 5 (su class)', hi: 'पाँचवाँ गण (सु आदि)' },
    6: { sa: 'तुदादिगणः', iast: 'tudādi', vikarana: 'श (अ, बिना गुण)', en: 'Class 6 (tud class)', hi: 'छठा गण (तुद् आदि)' },
    7: { sa: 'रुधादिगणः', iast: 'rudhādi', vikarana: 'श्नम् (न)', en: 'Class 7 (rudh class)', hi: 'सातवाँ गण (रुध् आदि)' },
    8: { sa: 'तनादिगणः', iast: 'tanādi', vikarana: 'उ', en: 'Class 8 (tan class)', hi: 'आठवाँ गण (तन् आदि)' },
    9: { sa: 'क्र्यादिगणः', iast: 'kryādi', vikarana: 'श्ना (ना)', en: 'Class 9 (krī class)', hi: 'नवाँ गण (क्री आदि)' },
    10: { sa: 'चुरादिगणः', iast: 'curādi', vikarana: 'णिच् + शप् (अय)', en: 'Class 10 (cur class)', hi: 'दसवाँ गण (चुर् आदि)' }
}

export const STATUS_LABEL: Record<ReviewStatus, Bi & { sa: string }> = {
    draft: { sa: 'प्रारूपम्', hi: 'प्रारूप', en: 'Draft' },
    'needs-review': { sa: 'समीक्षाधीनम्', hi: 'समीक्षा आवश्यक', en: 'Needs review' },
    verified: { sa: 'सत्यापितम्', hi: 'सत्यापित', en: 'Verified' },
    published: { sa: 'प्रकाशितम्', hi: 'प्रकाशित', en: 'Published' },
    archived: { sa: 'संगृहीतम्', hi: 'संग्रहीत', en: 'Archived' }
}

export const SKILL_LABEL: Record<SkillId, Bi & { sa: string }> = {
    devanagari: { sa: 'लिपिः', hi: 'देवनागरी पहचान', en: 'Devanagari recognition' },
    pronunciation: { sa: 'उच्चारणम्', hi: 'उच्चारण', en: 'Pronunciation' },
    vocabulary: { sa: 'शब्दज्ञानम्', hi: 'शब्दावली', en: 'Vocabulary' },
    reading: { sa: 'पठनम्', hi: 'पठन', en: 'Reading' },
    listening: { sa: 'श्रवणम्', hi: 'सुनना', en: 'Listening' },
    speaking: { sa: 'भाषणम्', hi: 'बोलना', en: 'Speaking' },
    conjugation: { sa: 'धातुरूपम्', hi: 'धातुरूप', en: 'Verb conjugation' },
    lakara: { sa: 'लकारज्ञानम्', hi: 'लकार पहचान', en: 'Lakāra recognition' },
    'verb-production': { sa: 'क्रियानिर्माणम्', hi: 'क्रिया बनाना', en: 'Verb production' },
    declension: { sa: 'शब्दरूपम्', hi: 'शब्दरूप', en: 'Declension' },
    vibhakti: { sa: 'विभक्तिज्ञानम्', hi: 'विभक्ति पहचान', en: 'Vibhakti identification' },
    sentence: { sa: 'वाक्यरचना', hi: 'वाक्य रचना', en: 'Sentence construction' },
    sandhi: { sa: 'सन्धिः', hi: 'सन्धि', en: 'Sandhi' },
    comprehension: { sa: 'अवबोधः', hi: 'पठन-बोध', en: 'Reading comprehension' }
}

export const vibhaktiName = (v: Vibhakti): string => VIBHAKTI_LABEL[v].sa

export const formLabel = (lang: 'hi' | 'en', label: GrammarLabel): string => lang === 'hi' ? label.hi : label.en
